import {
  BusinessFilter,
  createAssociationsBusinessFilter,
  DataViewKey,
  downloadManagerSelectionSelector,
  KpkMedia,
  useDataView,
} from "@keepeek/commons";
import { useTitleFieldInColumnsResolver } from "@keepeek/refront-components";
import { ColumnDef } from "@tanstack/react-table";
import { useRouter } from "next/router";
import { useSetRecoilState } from "recoil";

import { KIcon } from "../../../components/common/KIcon";

import { AssociationsWidgetProps } from ".";

type UseAssociationsWidget = {
  downloadAll: () => void;
  elementId: AssociationsWidgetProps["elementId"];
  elements: KpkMedia[] | undefined;
  filters: BusinessFilter[];
  columns: ColumnDef<KpkMedia, any>[];
};

export function useAssociationsWidget({
  elementIdWidget,
  mediaLinkIds,
  dataViewKey,
}: {
  elementIdWidget: AssociationsWidgetProps["elementId"];
  mediaLinkIds: AssociationsWidgetProps["mediaLinkIds"];
  dataViewKey: DataViewKey;
}): UseAssociationsWidget {
  // Standalone configuration:
  // If we do not have a elementIdWidget, look if we're on the element detail page to take this id instead
  // This way we can use a static elementIdWidget even in the element detail page
  const { query, pathname } = useRouter();
  const isPageElement: boolean = pathname.includes("element");
  const elementId: number | undefined =
    elementIdWidget ||
    (isPageElement && query.id && typeof query.id === "string" ? Number(query.id) : undefined);

  const setSelectionToDownloadManager = useSetRecoilState(
    downloadManagerSelectionSelector(dataViewKey),
  );

  const downloadAll = () => {
    if (elements) {
      setSelectionToDownloadManager(elements?.map(({ id }) => id));
    }
  };

  const associationsFilter = elementId
    ? createAssociationsBusinessFilter([elementId], mediaLinkIds)
    : createAssociationsBusinessFilter([]);
  const { elements } = useDataView({
    key: DataViewKey.ASSOCIATION,
    filters: [associationsFilter],
  });

  const columns = useTitleFieldInColumnsResolver({
    KIcon,
    showLinks: false,
    showTags: false,
    dataViewKey,
  });

  return {
    downloadAll,
    elementId,
    elements,
    filters: [associationsFilter],
    columns,
  };
}
